import { Controller } from "stimulus";
import { isEmpty } from "lodash";

import HtmldiffAsyncRunner from "../utilities/htmldiff_async_runner";

export default class extends Controller {
  static targets = [
    "versionCheckbox",
    "comparisonContainer",
    "diffToggle",
    "diffCheckbox",
    "checkboxContainer",
    "showAllCheckbox",
    "showAllToggle",
  ];

  static values = {
    recordType: String,
    compareUrl: String,
    currentValues: Object,
  };

  connect() {
    this.selectedAudits = [];
    this.asyncRunners = {};
    this.showDiff = false; // Start with diff disabled by default
    this.maxSelections = 3; // Max 3 historical versions (+ current = 4 total)

    // Render the version checkboxes once
    this.renderVersionCheckboxes();

    // Scroll checkbox container to bottom to show most recent versions
    if (this.hasCheckboxContainerTarget) {
      this.checkboxContainerTarget.scrollTop = this.checkboxContainerTarget.scrollHeight;
    }
  }

  renderVersionCheckboxes() {
    if (!this.hasCheckboxContainerTarget) return;

    const auditsData = JSON.parse(this.checkboxContainerTarget.dataset.audits || "[]");

    // Sort by created_at ASC (oldest first, chronological left-to-right)
    const sortedAudits = auditsData.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // Group by date to find last per day
    const auditsByDay = {};

    sortedAudits.forEach(audit => {
      const date = new Date(audit.created_at).toDateString();

      if (!auditsByDay[date]) {
        auditsByDay[date] = [];
      }
      auditsByDay[date].push(audit);
    });

    // Get the most recent audit for each day (last in each array since sorted ASC)
    const lastPerDay = new Set();

    Object.values(auditsByDay).forEach(dayAudits => {
      if (dayAudits.length > 0) {
        lastPerDay.add(dayAudits[dayAudits.length - 1].id);
      }
    });

    // Determine if we should show the "Show all updates" toggle
    const hasHiddenAudits = sortedAudits.length > lastPerDay.size;

    if (this.hasShowAllToggleTarget) {
      this.showAllToggleTarget.style.display = hasHiddenAudits ? "flex" : "none";
    }

    // Render checkboxes once
    const container = this.checkboxContainerTarget.querySelector(".flex.flex-wrap");

    if (!container) return;

    let html = "";

    sortedAudits.forEach(audit => {
      const isLastOfDay = lastPerDay.has(audit.id);
      const hiddenClass = isLastOfDay ? "" : "hidden";

      html += `
        <label class="flex items-center gap-2 p-2 bg-white rounded border border-gray-200 cursor-pointer hover:bg-gray-50 ${hiddenClass}"
               style="flex: 0 0 calc(20% - 0.5rem);"
               data-version-comparison-target="versionCheckbox"
               data-last-of-day="${isLastOfDay}"
               data-audit-id="${audit.id}">
          <input type="checkbox"
                 value="${audit.id}"
                 data-action="change->version-comparison#loadComparison" />
          <span class="text-sm">${audit.display} by ${audit.changed_by}</span>
        </label>
      `;
    });

    // Add current version checkbox at the end
    html += `
      <label class="flex items-center gap-2 p-2 bg-white rounded border border-gray-300"
             style="flex: 0 0 calc(20% - 0.5rem);">
        <input type="checkbox" checked disabled />
        <span class="text-sm font-medium">Current version</span>
      </label>
    `;

    container.innerHTML = html;
  }

  disconnect() {
    // Clean up async runners
    Object.values(this.asyncRunners).forEach(runner => {
      if (runner && runner.cleanup) {
        runner.cleanup();
      }
    });
  }

  loadComparison(event) {
    // Get all checked checkboxes (versionCheckboxTargets are labels, so get the inputs)
    // Only consider visible labels (not hidden ones)
    const visibleLabels = this.versionCheckboxTargets.filter(
      label => !label.classList.contains("hidden"),
    );
    const allInputs = visibleLabels
      .map(label => label.querySelector('input[type="checkbox"]'))
      .filter(input => input !== null);
    const checkedCheckboxes = allInputs.filter(cb => cb.checked);
    const selectedIds = checkedCheckboxes.map(cb => cb.value);

    // Enforce max selection limit
    if (checkedCheckboxes.length > this.maxSelections && event?.target) {
      // Uncheck the most recently checked box
      event.target.checked = false;
      return;
    }

    // Enable/disable unchecked boxes based on selection count
    allInputs.forEach(cb => {
      if (!cb.checked) {
        cb.disabled = checkedCheckboxes.length >= this.maxSelections;
        cb.parentElement.style.opacity = cb.disabled ? "0.5" : "1";
      }
    });

    if (selectedIds.length === 0) {
      this.comparisonContainerTarget.innerHTML = `
        <div class="text-center text-gray-500 py-8">
          Select versions above to begin comparison
        </div>
      `;
      // Hide diff toggle when no versions selected
      if (this.hasDiffToggleTarget) {
        this.diffToggleTarget.style.display = "none";
      }
      return;
    }

    // Show diff toggle whenever comparing at least 1 version
    if (this.hasDiffToggleTarget && this.hasDiffCheckboxTarget) {
      const hasVersions = selectedIds.length > 0;

      this.diffToggleTarget.style.display = hasVersions ? "flex" : "none";
    }

    // Get audit data from cached data (passed from HAML)
    const allAuditsData = JSON.parse(this.checkboxContainerTarget.dataset.audits || "[]");
    const selectedAudits = allAuditsData.filter(audit => selectedIds.includes(audit.id.toString()));

    // Get current record data from the page (from @record)
    const currentData = this.getCurrentRecordData();

    // Render comparison
    this.renderComparison(currentData, selectedAudits);
  }

  getCurrentRecordData() {
    // Get current values from the data attribute passed from the Rails controller
    return {
      id: "current",
      created_at: new Date().toISOString(),
      changed_by: "Current",
      values: this.currentValuesValue || {},
    };
  }

  renderComparison(currentData, audits) {
    // Sort audits by created_at ascending (oldest first) for proper cumulative diff
    const sortedAudits = [...audits].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );

    // Put current on the right: [...sortedAudits, currentData]
    const allVersions = [...sortedAudits, currentData];
    const fieldNames = this.getFieldNames(currentData, audits);
    // Show diff based on checkbox state (works for both single and multiple version comparisons)
    const showDiff = this.showDiff;

    let html = `
      <div class="grid grid-cols-${allVersions.length + 1} gap-4">
        <!-- Header row with version info - sticky at top and left -->
        <div class="font-semibold text-gray-700 p-2 bg-gray-50 rounded sticky top-0 left-0 z-20">
          Field
        </div>
    `;

    // Version headers - sticky at top
    allVersions.forEach((version, idx) => {
      const isCurrent = idx === allVersions.length - 1; // Current is now last

      html += `
        <div class="font-semibold text-gray-700 p-2 bg-gray-50 rounded sticky top-0 z-10">
          ${isCurrent ? "Current" : `Version ${idx + 1}`}
          ${
            showDiff && idx > 0
              ? `<div class="text-xs font-normal text-gray-400 italic">(diff against Version ${idx})</div>`
              : ""
          }
          ${
            isCurrent
              ? ""
              : `
            <div class="text-xs font-normal text-gray-500">
              ${this.formatDate(version.created_at)}
              <br/>
              by ${version.changed_by}
            </div>
          `
          }
        </div>
      `;
    });

    // Field rows
    fieldNames.forEach(fieldName => {
      // Check if this field has any differences across versions
      const values = allVersions.map(v => String(v.values[fieldName] || ""));
      const hasChanges = new Set(values).size > 1;
      const rowBgClass = hasChanges ? "bg-yellow-25" : "";

      // For sticky left column, use white bg if no changes, otherwise use the yellow bg
      const leftColBg = hasChanges ? "bg-yellow-25" : "bg-white";

      html += `
        <div class="font-medium text-gray-600 p-2 border-t border-gray-200 flex items-start sticky left-0 z-10 ${leftColBg}">
          ${this.formatFieldName(fieldName)}
        </div>
      `;

      allVersions.forEach((version, versionIdx) => {
        const fieldValue = version.values[fieldName];
        const cellId = `field-${fieldName}-version-${versionIdx}`;

        let displayValue;

        if (isEmpty(fieldValue)) {
          displayValue = '<span class="text-gray-400 italic">Empty</span>';
        } else {
          // All fields are plain text - escape HTML and preserve line breaks
          displayValue = this.escapeHtmlAndPreserveLineBreaks(String(fieldValue));
        }

        html += `
          <div class="p-2 border-t border-gray-200 ${rowBgClass}" id="${cellId}">
            <div class="trix-content text-sm" data-field-name="${fieldName}" data-version-idx="${versionIdx}">
              ${displayValue}
            </div>
          </div>
        `;
      });
    });

    html += "</div>";

    this.comparisonContainerTarget.innerHTML = html;

    // Calculate diffs for rich text fields when diff is enabled
    if (showDiff) {
      this.calculateDiffs(fieldNames, allVersions);
    }
  }

  calculateDiffs(fieldNames, allVersions) {
    // Only diff rich text fields (long text content)
    // For simple fields like "Yes/No", side-by-side is clearer than diff markup
    const richTextFields = this.getRichTextFields();

    richTextFields.forEach(fieldName => {
      // Use sequential timeline diff for all cases (2+ versions)
      this.calculateTimelineDiff(fieldName, allVersions);
    });
  }

  calculateTimelineDiff(fieldName, allVersions) {
    // Create a sequential timeline diff showing changes from the previous version
    // Each version column shows diffs from the immediately prior version

    for (let versionIdx = 0; versionIdx < allVersions.length; versionIdx++) {
      const cellId = `field-${fieldName}-version-${versionIdx}`;
      const currentVersionValue = allVersions[versionIdx].values[fieldName] || "";

      if (versionIdx === 0) {
        // First version (oldest) - show as-is, no diff
        const cell = document.getElementById(cellId);

        if (cell) {
          const contentDiv = cell.querySelector(".trix-content");

          if (contentDiv) {
            // All fields are plain text - escape HTML and preserve line breaks
            // Keep "Empty" placeholder if value is actually empty
            if (isEmpty(currentVersionValue)) {
              contentDiv.innerHTML = '<span class="text-gray-400 italic">Empty</span>';
            } else {
              contentDiv.innerHTML = this.escapeHtmlAndPreserveLineBreaks(currentVersionValue);
            }
          }
        }
      } else {
        // Show sequential diff from previous version to this version
        const previousVersionValue = allVersions[versionIdx - 1].values[fieldName] || "";
        const runnerKey = `${fieldName}-${versionIdx}-timeline`;

        // Special case: if current is empty but previous has content, show all as deleted
        if (isEmpty(currentVersionValue) && !isEmpty(previousVersionValue)) {
          const cell = document.getElementById(cellId);

          if (cell) {
            const contentDiv = cell.querySelector(".trix-content");

            if (contentDiv) {
              // Show previous value entirely as deleted (strikethrough)
              const previousWithBreaks = this.escapeHtmlAndPreserveLineBreaks(previousVersionValue);

              contentDiv.innerHTML = this.wrapDiffHtml(`<del>${previousWithBreaks}</del>`);
            }
          }
        } else if (isEmpty(previousVersionValue) && !isEmpty(currentVersionValue)) {
          // Special case: if previous is empty but current has content, show all as added
          const cell = document.getElementById(cellId);

          if (cell) {
            const contentDiv = cell.querySelector(".trix-content");

            if (contentDiv) {
              // Show current value entirely as inserted (green background)
              const currentWithBreaks = this.escapeHtmlAndPreserveLineBreaks(currentVersionValue);

              contentDiv.innerHTML = this.wrapDiffHtml(`<ins>${currentWithBreaks}</ins>`);
            }
          }
        } else {
          // Normal diff calculation
          if (!this.asyncRunners[runnerKey]) {
            this.asyncRunners[runnerKey] = new HtmldiffAsyncRunner(diff => {
              const cell = document.getElementById(cellId);

              if (cell) {
                const contentDiv = cell.querySelector(".trix-content");

                if (contentDiv) {
                  contentDiv.innerHTML = this.wrapDiffHtml(diff);
                }
              }
            });
          }

          // Convert newlines to <br> tags BEFORE diffing so they're preserved in the diff
          // Also collapse multiple consecutive newlines to max 2
          const previousWithBreaks = previousVersionValue
            .replace(/\n{3,}/g, "\n\n")
            .replace(/\n/g, "<br>");
          const currentWithBreaks = currentVersionValue
            .replace(/\n{3,}/g, "\n\n")
            .replace(/\n/g, "<br>");

          // Diff from previous version to this version (sequential)
          this.asyncRunners[runnerKey].calculateDiff(previousWithBreaks, currentWithBreaks);
        }
      }
    }
  }

  getFieldNames(currentData, audits) {
    const allFieldNames = new Set();

    // Add current fields
    Object.keys(currentData.values || {}).forEach(key => allFieldNames.add(key));

    // Add fields from all audits
    audits.forEach(audit => {
      Object.keys(audit.values || {}).forEach(key => allFieldNames.add(key));
    });

    return Array.from(allFieldNames).sort();
  }

  getRichTextFields() {
    // These are the rich text fields that should use diff comparison
    if (this.recordTypeValue === "policy") {
      return ["policy_language", "restriction_language"];
    } else if (this.recordTypeValue === "prior_auth") {
      return ["criteria_language"];
    }
    return [];
  }

  formatFieldName(fieldName) {
    return fieldName.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
  }

  formatDate(dateString) {
    const date = new Date(dateString);

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  }

  escapeHtml(text) {
    const div = document.createElement("div");

    div.textContent = text;
    return div.innerHTML;
  }

  escapeHtmlAndPreserveLineBreaks(text) {
    // First escape HTML to prevent XSS
    const escaped = this.escapeHtml(text);
    // Then convert newlines to <br> tags, collapsing multiple consecutive newlines to max 2

    return escaped.replace(/\n{3,}/g, "\n\n").replace(/\n/g, "<br>");
  }

  wrapDiffHtml(diff) {
    // Just return the diff with a wrapper div for styling, not a nested trix-content
    return `<div class="htmldiff-output">${diff}</div>`;
  }

  toggleDiffView() {
    // Read the checkbox state
    this.showDiff = this.diffCheckboxTarget.checked;
    // Reload the comparison with the new setting
    this.loadComparison();
  }

  toggleShowAll() {
    const showAll = this.showAllCheckboxTarget.checked;

    let anyCheckboxUnchecked = false;

    // Just toggle visibility of non-last-of-day checkboxes
    this.versionCheckboxTargets.forEach(label => {
      const isLastOfDay = label.dataset.lastOfDay === "true";

      if (!isLastOfDay) {
        if (showAll) {
          label.classList.remove("hidden");
        } else {
          label.classList.add("hidden");
          // Uncheck hidden checkboxes
          const checkbox = label.querySelector('input[type="checkbox"]');

          if (checkbox && checkbox.checked) {
            checkbox.checked = false;
            anyCheckboxUnchecked = true;
          }
        }
      }
    });

    // Only reload comparison if we unchecked something (which changes the selection)
    if (anyCheckboxUnchecked) {
      this.loadComparison();
    }
  }
}
